import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { PortalContext } from '../../../../context/PortalContext';
import { isEniNiceMember } from '../../../../frontends/GuestExperience/utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  getEnrollmentIdUnavailableContent,
  getNiceDecIdStatus,
} from '../../../../utils/providerDetails.utils';
import { capitalizeAllFirstCharacters } from '../../../../utils/providerNames.utils';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import { isTruthy } from '../../../../utils/string.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import { NetworkAffiliationLabels } from '../../../Constants';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import {
  networkAffiliationCardContainer,
  networkAffiliationCardContentContainer,
  networkAffiliationCardEnrollmentLabelStyle,
  networkAffiliationCardEnrollmentUnavailableMessageStyle,
  networkAffiliationCardEnrollmentUnavailablePhoneLinkStyle,
  networkAffiliationCardEnrollmentValueStyle,
  networkAffiliationCardNameContainer,
  networkAffiliationCardNameStyle,
  networkAffiliationCardTextStyle,
} from './NetworkAffiliations.style';

export interface NetworkAffiliation {
  networkAffiliationId: string;
  networkAffiliationName: string;
  acceptingNewPatients: boolean;
  pcpId?: string;
  specialities?: Array<{ id: string; value: string }>;
  medicalgrpaffilIdName?: string;
  pcpIndicator?: boolean;
  networkAffiliationTypeCode?: string;
  decIdentifier?: string;
}

interface NetworkAffiliationsCardProps {
  affiliation: NetworkAffiliation;
  testId?: string;
  onClick?: () => void;
  providerType?: string;
}

export const NetworkAffiliationsCard: React.FC<
  NetworkAffiliationsCardProps
> = ({
  affiliation,
  testId = 'network-affiliation-card',
  onClick,
  providerType,
}) => {
  const { t } = useTranslation();
  const { portalData } = useContext(PortalContext);
  const uiMessaging = useLagoon('ui-messaging')();

  const [
    disableNetworkAffiliationANPEnhancements,
    enableGuestNiceDecIdEnrollmentFlag,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS
      .DISABLE_NETWORK_AFFILIATION_ACCEPTING_NEW_PATIENTS_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GUEST_NICE_DEC_ID_ENROLLMENT,
  ]);

  const isGuestUser = !!portalData?.portalConfig?.isGuestUser;
  const { lineOfBusiness, searchDirectory, planCategory } =
    getCurrentMember() || {};
  const allConfigs = useLagoon('config');
  const eniNicePlanCategoryName = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.ENI_NICE_PLAN_CATEGORY_NAME
  );
  const isEniNiceGuestMember = isEniNiceMember(
    lineOfBusiness,
    searchDirectory,
    planCategory,
    eniNicePlanCategoryName
  );

  const status = affiliation.acceptingNewPatients
    ? t(NetworkAffiliationLabels.ACCEPTING_NEW_PATIENTS)
    : t(NetworkAffiliationLabels.NOT_ACCEPTING_NEW_PATIENTS);

  const pcpIndicator = affiliation.pcpIndicator;

  const isEligibleForPcp =
    String(affiliation.pcpIndicator).toLowerCase() === 'true' &&
    affiliation.acceptingNewPatients;

  const useNiceDecIdFlow =
    enableGuestNiceDecIdEnrollmentFlag &&
    isGuestUser &&
    isEniNiceGuestMember &&
    isEligibleForPcp;

  const niceDecIdStatus = useNiceDecIdFlow
    ? getNiceDecIdStatus(affiliation.decIdentifier)
    : null;
  const isEnrollmentIdGap = Boolean(niceDecIdStatus?.isGap);

  const enrollmentId = useNiceDecIdFlow
    ? niceDecIdStatus?.value
    : affiliation.pcpId || t('Not available / unknown');

  const enrollmentIdUnavailableContent = getEnrollmentIdUnavailableContent(
    uiMessaging,
    {
      message: ConstantsLagoon.UI_MESSAGING.ENROLLMENT_ID_UNAVAILABLE_MESSAGE,
      phoneNumber:
        ConstantsLagoon.UI_MESSAGING.ENROLLMENT_ID_UNAVAILABLE_PHONE_NUMBER,
    }
  );

  const affiliationName = capitalizeAllFirstCharacters(
    affiliation.networkAffiliationName?.toLowerCase() ||
      t('Not available / unknown')
  );

  const isOrganization = providerType?.toLowerCase() === 'organization';

  return (
    <Flex
      css={networkAffiliationCardContainer}
      data-auto-testid={testId}
      data-testid={testId}
      direction="column"
    >
      <Flex
        css={networkAffiliationCardNameContainer}
        direction="column"
        onClick={onClick}
      >
        <Text css={networkAffiliationCardNameStyle} fontWeight="$semibold">
          <span title={affiliationName}>{affiliationName}</span>
        </Text>
      </Flex>
      <div
        style={{
          width: '100%',
          height: '0px',
          borderBottom: '1px solid #CBCCCD',
        }}
      />
      <Flex css={networkAffiliationCardContentContainer} direction="column">
        {!(isOrganization && disableNetworkAffiliationANPEnhancements) && (
          <Text css={networkAffiliationCardTextStyle} fontWeight="$semibold">
            {status}
          </Text>
        )}
        <Flex
          css={{
            gap: '4px',
            alignItems: 'baseline',
            overflow: 'hidden',
            flexWrap: 'nowrap',
            minWidth: 0,
          }}
        >
          <Text
            css={networkAffiliationCardEnrollmentLabelStyle}
            fontWeight="$semibold"
          >
            {isTruthy(pcpIndicator)
              ? t('NETWORK_AFFILIATION.ENROLLMENT_ID')
              : t('PROVIDER_LOCATIONS_DETAILS.PROVIDER_ID')}
          </Text>
          <Text
            css={networkAffiliationCardEnrollmentValueStyle}
            fontWeight="$normal"
          >
            <span
              title={
                isEnrollmentIdGap
                  ? t(
                      'NETWORK_AFFILIATION.ENROLLMENT_ID_TEMPORARILY_UNAVAILABLE'
                    )
                  : enrollmentId
              }
            >
              {isEnrollmentIdGap
                ? t('NETWORK_AFFILIATION.ENROLLMENT_ID_TEMPORARILY_UNAVAILABLE')
                : enrollmentId}
            </span>
          </Text>
        </Flex>
        {isEnrollmentIdGap && (
          <Text
            css={networkAffiliationCardEnrollmentUnavailableMessageStyle}
            data-testid="network-affiliation-card-enrollment-id-unavailable-message"
          >
            {enrollmentIdUnavailableContent.prefix}
            <LinkWithTracking
              css={networkAffiliationCardEnrollmentUnavailablePhoneLinkStyle}
              data-auto-testid="network-affiliation-card-enrollment-id-unavailable-phone-link"
              data-testid="network-affiliation-card-enrollment-id-unavailable-phone-link"
              hideIcon
              href={`tel:${enrollmentIdUnavailableContent.phoneNumber.replace(
                /[^\d+]/g,
                ''
              )}`}
            >
              {enrollmentIdUnavailableContent.phoneNumber}
            </LinkWithTracking>
            {enrollmentIdUnavailableContent.suffix}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
